import { useMemo, useEffect, useCallback } from 'react'
import {
  TableBody,
} from 'src/components/TableComponents'
import { NumberFmt, measureUnit, formatNumberBR } from 'src/utils'
import { useTheme } from 'styled-components'
import * as S from '../styles'
import {
  SalesReceivementProduct, OverviewProductStatusEnum, ProductStatusEnum, SalesBatchesState, SalesBatchesValue,
} from '../../../ReceivementTypes'
import { ReceivementProductWrapper } from '../../ReceivementProductWrapper'
import notes from 'src/assets/images/other-images/notes.svg'
import check from 'src/assets/images/other-images/check.svg'
import { ProductTableRow } from './ProductTableRow'
import { validateAcceptedQuantity, arAcceptCeiling } from './validateAcceptedQuantity'
import { isKgOverWithinTolerance } from './isKgOverWithinTolerance'
import { useObservationModal } from 'src/features/receivement/pages/ReceivementPurchase/hooks/useObservationModal'
import { useSelector } from 'src/utils/store'
import { selectBatchesStateByIds, selectSalesOverview } from 'src/features/receivement/ReceivementSelectors'
import { useDispatch, shallowEqual, useStore } from 'react-redux'
import { actions } from 'src/features/receivement/ReceivementState'

interface Props {
  product: SalesReceivementProduct
}

const PREC = 1000

const ProductTable = ({ product }: Props) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const store = useStore()
  const { openObservationModal, ObservationModalComponent } = useObservationModal({ id: product.idItemOrder, productObservation: product.observation })
  const salesOverview = useSelector(selectSalesOverview)

  const productBatchIds = useMemo(
    () => product.batches.map((b) => b.idsBatchItemOrder.join(',')),
    [product.batches],
  )

  const batchesState = useSelector(
    useMemo(() => selectBatchesStateByIds(productBatchIds), [productBatchIds]),
    shallowEqual,
  )

  const {
    totalAccepted, totalReturnQuantity, totalSwapQuantity,
  } = useMemo(() => {
    let accepted = 0
    let returnQty = 0
    let swap = 0

    productBatchIds.forEach((id) => {
      const b = batchesState[id]
      if (b) {
        accepted += b.acceptedQuantity || 0
        returnQty += b.returnQuantity || 0
        swap += b.swapQuantity || 0
      }
    })

    return {
      totalAccepted: accepted, totalReturnQuantity: returnQty, totalSwapQuantity: swap,
    }
  }, [batchesState, productBatchIds])

  const {
    receivementTotal, acceptsNfdWithoutBreakingBox, isQuantityValid, tooltipSuggestions, cap,
  } = useMemo(() => {
    const total = product.damageQuantity + product.usefulQuantity
    const enforceBox = !!salesOverview?.allowPartialReturn && !salesOverview?.allowBreakUnitOnInvoiceReturn

    const { cap: capValue, isValid, suggestions } = validateAcceptedQuantity({
      totalReceived: total,
      expectedQuantity: product.expectedQuantity,
      totalAccepted,
      quantityPerBox: product.quantityPerBox,
      enforceBox,
      isKg: product.isKg,
      returnQuantity: product.returnQuantity || 0,
    })

    return {
      receivementTotal: total,
      acceptsNfdWithoutBreakingBox: enforceBox,
      isQuantityValid: isValid,
      tooltipSuggestions: suggestions,
      cap: capValue,
    }
  }, [product, salesOverview, totalAccepted])

  useEffect(() => {
    const fullBatchesState = (store.getState() as any).receivement.batchesStateByBatchItemOrderIds
    dispatch(actions.setTotalReceivementStockDays({ batchesState: fullBatchesState }))
  }, [batchesState, dispatch, store])

  useEffect(() => {
    dispatch(actions.SetQuantityAcceptedIsValid({ modelId: product.modelId, quantityAcceptedIsValid: isQuantityValid }))
  }, [dispatch, isQuantityValid, product.modelId])

  const getDifference = () => {
    const value = product.usefulQuantity - product.expectedQuantity
    const prefix = value > 0 ? '+' : ''
    const color = value > 0 ? theme.colors.confirm : value < 0 ? theme.colors.red : 'inerit'

    return <S.Text style={{ color }}><NumberFmt prefix={prefix} value={value} sufix={`${measureUnit(product.isKg)}`} /></S.Text>
  }

  const returnQuantity = () => {
    const value = product.returnQuantity > 0 ? product.returnQuantity : '-'
    return <S.NumberText isNegative={product.returnQuantity > 0}><NumberFmt prefix='-' value={value} sufix={`${measureUnit(product.isKg)}`} /></S.NumberText>
  }

  const getTotalAccepted = () => {
    const fmtQty = (value: number) => {
      const formatted = product.isKg ? formatNumberBR(value, 3) : formatNumberBR(value)
      return `${formatted} ${measureUnit(product.isKg)}`
    }

    if (isQuantityValid && !acceptsNfdWithoutBreakingBox) {
      return <NumberFmt value={totalAccepted} sufix={`${measureUnit(product.isKg)}`} />
    }

    const getAcceptedQuantitySuggestion = () => {
      // NFD = Rejeitado AR + max(0, Esperado − Aceito); aceitar ≥ esperado deixa nfd = Rejeitado AR,
      // então "a partir de esperado" só é saída válida se o AR for 0 ou múltiplo cheio da caixaria
      const nfdLocal = (product.returnQuantity || 0) + Math.max(0, product.expectedQuantity - totalAccepted)
      const arInt = Math.round((product.returnQuantity || 0) * PREC)
      const boxLocalInt = Math.round(product.quantityPerBox * PREC)
      const arIsBoxValid = arInt === 0 || (boxLocalInt > 0 && arInt % boxLocalInt === 0 && arInt >= boxLocalInt)
      const thresholdReachable = arIsBoxValid && receivementTotal >= product.expectedQuantity
      const fromThreshold = product.expectedQuantity
      if (tooltipSuggestions.length >= 2) {
        const [s1, s2] = tooltipSuggestions
        return `Aceite ${fmtQty(s1.value)} (devolve ${s1.boxes} ${s1.boxes === 1 ? 'caixa' : 'caixas'}) ou ${fmtQty(s2.value)} (devolve ${s2.boxes} ${s2.boxes === 1 ? 'caixa' : 'caixas'})`
      }
      if (tooltipSuggestions.length === 1) {
        const [s] = tooltipSuggestions
        const nfdBelowOneBox = Math.round(nfdLocal * PREC) < Math.round(product.quantityPerBox * PREC)
        const boxSuggestion = `Aceite ${fmtQty(s.value)} para devolver ${s.boxes} ${s.boxes === 1 ? 'caixa' : 'caixas'}`
        return nfdBelowOneBox && thresholdReachable ? `${boxSuggestion}, ou a partir de ${fmtQty(fromThreshold)}` : boxSuggestion
      }
      // Sem sugestão de caixaria: o problema restante é o teto da AR ("cap vence sempre").
      // Sugestões, quando existem, já são ≤ esperado ≤ cap e resolvem o teto por construção
      if (Math.round(totalAccepted * PREC) > Math.round(cap * PREC)) {
        return `Deve ser aceito no máximo ${fmtQty(cap)}`
      }
      return thresholdReachable ? `Aceite a partir de ${fmtQty(fromThreshold)}` : 'Quantidade inválida'
    }

    return isQuantityValid ? (
      <S.StatusQuantity
        backgroundColor={theme.colors.green}
        color={theme.colors.darkGray}
        data-testid='valid-quantity'
      >
        <NumberFmt value={totalAccepted} sufix={`${measureUnit(product.isKg)}`} />
      </S.StatusQuantity>
    ) : (
      <S.CellTooltip title={getAcceptedQuantitySuggestion()} arrow>
        <S.StatusQuantity
          backgroundColor={theme.colors.red}
          color={theme.colors.white}
          data-testid='invalid-quantity'
        >
          <NumberFmt value={totalAccepted} sufix={`${measureUnit(product.isKg)}`} />
        </S.StatusQuantity>
      </S.CellTooltip>
    )
  }

  const getTotalToReturn = () => {
    if (!acceptsNfdWithoutBreakingBox) {
      return <NumberFmt value={totalReturnQuantity} sufix={`${measureUnit(product.isKg)}`} />
    }

    const fmtQty = (value: number) => {
      const formatted = product.isKg ? formatNumberBR(value, 3) : formatNumberBR(value)
      return `${formatted} ${measureUnit(product.isKg)}`
    }

    const nfdQty = (product.returnQuantity || 0) + Math.max(0, product.expectedQuantity - totalAccepted)
    // Fora NFD = parcela devolvida além da nota; aceite acima do esperado fica em estoque, não é devolução
    const effectiveExcess = Math.max(0, receivementTotal - Math.max(totalAccepted, product.expectedQuantity) - (product.returnQuantity || 0))

    const returnQuantityCell = (
      <S.StatusQuantity
        backgroundColor={isQuantityValid ? theme.colors.green : theme.colors.red}
        color={isQuantityValid ? theme.colors.darkGray : theme.colors.white}
        data-testid='total-return-quantity'
      >
        <NumberFmt value={totalReturnQuantity} sufix={`${measureUnit(product.isKg)}`} />
      </S.StatusQuantity>
    )

    const anyBatchHasAction = productBatchIds.some((id) => !!batchesState[id]?.option)

    if (!anyBatchHasAction) {
      return returnQuantityCell
    }

    return (
      <S.CellTooltip
        title={(
          <>
            <div>Qtd. NFD - {fmtQty(nfdQty)}</div>
            {effectiveExcess > 0 && <div>Qtd. Fora NFD - {fmtQty(effectiveExcess)}</div>}
          </>
        )}
        arrow
      >
        {returnQuantityCell}
      </S.CellTooltip>
    )
  }

  const getTotal = () => {
    const value = product.damageQuantity + product.usefulQuantity
    return <NumberFmt value={value} sufix={`${measureUnit(product.isKg)}`} />
  }

  const handleAcceptMaximumCard = useCallback(() => {
    const totalReceived = product.damageQuantity + product.usefulQuantity
    const excess = Math.max(0, totalReceived - product.expectedQuantity)
    const totalDamage = product.damageQuantity

    const boxInt = Math.round(product.quantityPerBox * PREC)
    const returnQtyInt = Math.round((product.returnQuantity || 0) * PREC)
    // shortage: units expected but not received — they go to NFD automatically
    const shortageInt = Math.max(0, Math.round(product.expectedQuantity * PREC) - Math.round(totalReceived * PREC))
    // damage not covered by excess — also needs to go to NFD
    const uncoveredDamageInt = Math.max(0, Math.round(totalDamage * PREC) - Math.round(excess * PREC))
    // NFD base includes Rejeitado AR (product.returnQuantity) plus shortage and uncovered damage
    const naturalNFDInt = returnQtyInt + shortageInt + uncoveredDamageInt
    const roundedNFDInt = salesOverview?.allowBreakUnitOnInvoiceReturn || boxInt === 0
      ? naturalNFDInt
      : Math.ceil(naturalNFDInt / boxInt) * boxInt
    const excessInt = Math.round(excess * PREC)
    // Excess is returned in full so accepted never exceeds expected; the excess return
    // is outside the NFD and has no caixaria constraint (validation only covers the NFD)
    // shortage/returnQty are not received physically; only (roundedNFD - shortage - returnQty) comes from batches
    let remaining = excessInt + roundedNFDInt - shortageInt - returnQtyInt

    // "Cap vence sempre": o total aceito nunca ultrapassa min(recebido, teto AR).
    // O excedente acima do teto e devolvido FORA da NFD (sem arredondar em caixa).
    const capInt = Math.round(
      Math.min(totalReceived, arAcceptCeiling(product.expectedQuantity, product.isKg)) * PREC,
    )
    const overCapInt = Math.max(0, Math.round(totalReceived * PREC) - capInt)
    remaining = Math.max(remaining, overCapInt)

    const batchReturnsInt: Record<string, number> = {}

    // Phase 1: consume damage first
    product.batches.forEach((batch) => {
      if (batch.damageQuantity > 0 && remaining > 0) {
        const key = batch.idsBatchItemOrder.join(',')
        const damageToReturn = Math.min(Math.round(batch.damageQuantity * PREC), remaining)
        batchReturnsInt[key] = (batchReturnsInt[key] || 0) + damageToReturn
        remaining -= damageToReturn
      }
    })

    const expSort = (a: { expirationDate?: string }, b: { expirationDate?: string }) => {
      if (!a.expirationDate) return 1
      if (!b.expirationDate) return -1
      return a.expirationDate < b.expirationDate ? -1 : a.expirationDate > b.expirationDate ? 1 : 0
    }

    // Phase 2: distribute remaining return by expiration ASC (soonest first)
    const phase2Order = [...product.batches].sort(expSort)

    phase2Order.forEach((batch) => {
      if (remaining <= 0) return
      const key = batch.idsBatchItemOrder.join(',')
      const alreadyInt = batchReturnsInt[key] || 0
      const available = Math.round(batch.totalQuantity * PREC) - alreadyInt
      if (available <= 0) return
      const toReturn = Math.min(available, remaining)
      batchReturnsInt[key] = alreadyInt + toReturn
      remaining -= toReturn
    })

    const fullBatchesState = (store.getState() as any).receivement.batchesStateByBatchItemOrderIds
    const newBatchesState: SalesBatchesState = {}

    product.batches.forEach((batch) => {
      const key = batch.idsBatchItemOrder.join(',')
      const batchState = fullBatchesState[key]
      const batchReturnInt = batchReturnsInt[key] || 0
      const totalInt = Math.round(batch.totalQuantity * PREC)
      const batchReturn = batchReturnInt / PREC
      const batchAccepted = (totalInt - batchReturnInt) / PREC

      let update: Partial<SalesBatchesValue>

      if (batchReturnInt >= totalInt) {
        update = {
          option: 'REJECT_ALL',
          acceptedQuantity: 0,
          returnQuantity: batch.totalQuantity,
          swapQuantity: undefined,
          acceptDamage: false,
          stockDays: 0,
        }
      } else if (batchReturnInt === 0 || (batchReturnInt === Math.round(batch.damageQuantity * PREC) && batch.damageQuantity > 0)) {
        update = {
          option: 'ACCEPT_ALL',
          acceptedQuantity: batch.usefulQuantity,
          returnQuantity: batch.damageQuantity,
          acceptDamage: false,
          stockDays: product.salesPerDay !== 0 ? batch.usefulQuantity / product.salesPerDay : 0,
        }
      } else {
        update = {
          option: 'PARTIAL',
          acceptedQuantity: batchAccepted,
          returnQuantity: batchReturn,
          acceptDamage: batchReturn > batch.damageQuantity,
          stockDays: product.salesPerDay !== 0 ? batchAccepted / product.salesPerDay : 0,
        }
      }

      newBatchesState[key] = { ...batchState, ...update }
      dispatch(actions.removeBatchErrorId(batch.idsBatchItemOrder.toString()))
    })

    dispatch(actions.updateBatchState(newBatchesState))
  }, [product, dispatch, store, salesOverview?.allowBreakUnitOnInvoiceReturn])

  const status = useMemo(() => {
    const notDivergent = product.status === OverviewProductStatusEnum.CORRECT
      || isKgOverWithinTolerance({
        isKg: product.isKg,
        status: product.status,
        damageQuantity: product.damageQuantity,
        usefulQuantity: product.usefulQuantity,
        expectedQuantity: product.expectedQuantity,
      })
    if (notDivergent && product.returnQuantity === 0) {
      return ProductStatusEnum.CORRECT
    }
    return ProductStatusEnum.DIVERGENT
  }, [
    product.status,
    product.returnQuantity,
    product.isKg,
    product.damageQuantity,
    product.usefulQuantity,
    product.expectedQuantity,
  ])

  const getProductInfo = () => `
    ${product.productDescription}
    (Modelo: ${product.productCode},
    Caixaria: ${product.quantityPerBox} ${measureUnit(product.isKg).toLocaleLowerCase()})
  `

  return (
    <ReceivementProductWrapper status={status} buyer={product.buyerName}>
      <S.Table>
        <S.TableHeader
          columnInformation={[
            { columnName: 'AR' },
            { columnName: 'NF' },
            { columnName: 'Produto' },
            { columnName: 'Anotações' },
            { columnName: '' },
            { columnName: 'D.E. Atual', tooltipMessage: 'Dias de estoque atual' },
          ]}
          flexColumns={[
            0.05,
            0.05,
            0.7,
            0.1,
            0.1,
            0.1,
          ]}
        />
        <TableBody>
          <S.TableBodyRow backgroundColor={theme.colors.white}>
            <S.TableBodyCell flex={0.05}>{product.idReceivementAuthorization}</S.TableBodyCell>
            <S.TableBodyCell flex={0.05}>{product.invoiceNumber}</S.TableBodyCell>
            <S.TableBodyCell flex={0.7}>{getProductInfo()}</S.TableBodyCell>
            <S.TableBodyCell flex={0.1}>
              <S.Button onClick={() => openObservationModal()}>
                {product.observation
                  && (
                  <S.ImageContainer data-testid='annotation-icon-check'>
                    <S.ImageCheck src={check} />
                  </S.ImageContainer>
                  )}
                <S.Image src={notes} data-testid='annotation-icon'/>
              </S.Button>
            </S.TableBodyCell>
            <S.TableBodyCell flex={0.1}>
              {salesOverview?.allowInvoiceReturn && salesOverview?.allowPartialReturn && status !== ProductStatusEnum.CORRECT && (
                <S.CellTooltip
                  title={acceptsNfdWithoutBreakingBox
                    ? 'Auto-calcula o máximo a aceitar: recusa avarias primeiro, depois lotes de validade mais próxima, respeitando a caixaria.'
                    : 'Auto-calcula o máximo a aceitar: recusa avarias primeiro, depois lotes de validade mais próxima.'}
                  arrow
                >
                  <S.AcceptMaxButtonWrapper>
                    <S.AcceptMaxButton
                      size='small'
                      title='Aceitar máx.'
                      bgColor='blue'
                      hoverColor='darkBlueHover'
                      letterColor='white'
                      onClick={handleAcceptMaximumCard}
                    />
                  </S.AcceptMaxButtonWrapper>
                </S.CellTooltip>
              )}
            </S.TableBodyCell>
            <S.TableBodyCell flex={0.1}>{product.currentStockDays}</S.TableBodyCell>
          </S.TableBodyRow>
        </TableBody>
        <S.TableHeader
          backgroundColor={theme.colors.gray}
          columnInformation={[
            { columnName: 'Qtd. Recebida' },
            { columnName: 'Qtd. Avariada' },
            { columnName: 'Qtd. Útil' },
            { columnName: 'Data Validade' },
            { columnName: 'Motivo Avaria' },
            { columnName: 'Ação' },
            { columnName: 'Qtd. a Aceitar' },
            { columnName: 'Qtd. a Devolver' },
            { columnName: 'Qtd. a Trocar', hidden: !salesOverview?.allowProductExchange },
            { columnName: 'D.E.' },
          ]}
          flexColumns={[
            1,
            1,
            1,
            1,
            1.5,
            1.5,
            1,
            1.5,
            1,
            1,
          ]}
        />
        <TableBody>
          {product.batches.map((batch) => (
            <ProductTableRow
              key={batch.idsBatchItemOrder.toString()}
              batch={batch}
              product={product}
              productStatus={status}
              allowProductExchange={salesOverview?.allowProductExchange}
            />
          ))}
        </TableBody>
        <S.TableHeader
          backgroundColor={theme.colors.gray}
          columnInformation={[
            { columnName: 'Esperado' },
            { columnName: 'Recebido' },
            { columnName: 'Avariado' },
            { columnName: 'Útil' },
            { columnName: 'Rejeitado AR', tooltipMessage: 'Quantidade a devolver solicitada durante a conferência da nota.', alert: product.returnQuantity > 0 },
            { columnName: '(Útil - Esperado)' },
            { columnName: 'Aceito' },
            { columnName: 'Total a Devolver', tooltipMessage: acceptsNfdWithoutBreakingBox ? 'Total a devolver ao fornecedor. Para fornecedores que aceitam somente caixaria, a devolução — excluindo o excesso recebido — deve ser de caixas completas (mínimo 1 caixa).' : undefined },
            { columnName: 'Total a Trocar', hidden: !salesOverview?.allowProductExchange },
            {
              columnName: 'Total D.E.',
              tooltipMessage: 'DE atual + D.E. dos lotes aceitos',
            },
          ]}
          flexColumns={[
            1,
            1,
            1,
            1,
            1.5,
            1.5,
            1,
            1.5,
            1,
            1,
          ]}
        />
        <S.TableBody>
          <S.TableBodyRow backgroundColor={theme.colors.white}>
            <S.TableBodyCell flex={1}>
              <NumberFmt value={product.expectedQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1}>{getTotal()}</S.TableBodyCell>
            <S.TableBodyCell flex={1}>
              <NumberFmt value={product.damageQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1}>
              <NumberFmt value={product.usefulQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1.5}>{returnQuantity()}</S.TableBodyCell>
            <S.TableBodyCell flex={1.5}>{getDifference()}</S.TableBodyCell>
            <S.TableBodyCell flex={1}>{getTotalAccepted()}</S.TableBodyCell>
            <S.TableBodyCell flex={1.5}>{getTotalToReturn()}</S.TableBodyCell>
            {
              salesOverview?.allowProductExchange && (
                <S.TableBodyCell flex={1}>
                  <NumberFmt value={totalSwapQuantity} sufix={`${measureUnit(product.isKg)}`} />
                </S.TableBodyCell>
              )
            }
            <S.TableBodyCell flex={1}><S.Text>{`${product.totalArStockDays?.toFixed(1)} / ${product.totalReceivementStockDays?.toFixed(1)}`}</S.Text></S.TableBodyCell>
          </S.TableBodyRow>
        </S.TableBody>
      </S.Table>

      {ObservationModalComponent}
    </ReceivementProductWrapper>
  )
}

export { ProductTable }
